import { useEffect } from "react";
import Script from "next/script";
import { NextSeo } from "next-seo";
import { getClientIp } from "request-ip";

import DefaultChannelView from "components/channels/screens/DefaultView";
import TelegramChannelView from "components/channels/screens/TelegramView";
import WhatsappChannelView from "components/channels/screens/WhatsappView";

import * as Track from "@qoohoo/common/utils/analytics/track";
import { domain, websiteDomain } from "constants/domain";
import { getOGImageType } from "@qoohoo/common/utils/url";
import { getRedirectPath } from "library/helpers/url";
import {
  STANDARD_EVENTS,
  trackStandardEvent,
} from "@qoohoo/common/utils/analytics/pixel";
import { pageview } from "@qoohoo/common/constants/analytics";
import { addTagsInRecording } from "@qoohoo/common/utils/analytics/clarity";
import useStore from "store/store";
import httpm from "@qoohoo/common/utils/api/middleLayer/httpm";
import {
  checkIsWhiteTheme,
  TELEGRAM_CHANNEL,
  WHATSAPP_CHANNEL,
} from "library/helpers/customConfigs/creatorConfig/helpers/checkIsWhiteTheme";
import { isFinanceChannel } from "@qoohoo/common/helpers/channel";
import { getCustomCreatorConfig } from "library/helpers/customConfigs/creatorConfig";
import FBPixelScript from "components/integrations/FBPixelScript";
import handleServerSideProps from "components/templates/handleServerSideProps";
import GoogleAnalyticsScript from "components/integrations/GoogleAnalyticsScript";
import OrderSummaryModal from "../../channels/modals/OrderSummaryModal";

export default function DefaultChannelTemplate(props) {
  const {channelDetails, country, isWhiteTheme, customTemplateConfig = {}} = props;

  const {
    uid,
    name,
    description,
    short_url_flutter,
    category,
    is_paid,
    is_private,
    cta_button,
    has_limited_seats,
    total_seats_left,
    creator,
    is_telegram,
    is_whatsapp,
  } = channelDetails;

  const customCreatorConfig = getCustomCreatorConfig(creator.uid) || {};
  const customConfig = {
    ...customCreatorConfig,
    ...customTemplateConfig
  };

  const isCategoryFinance = isFinanceChannel(category, name, description);

  const channelImageUrl = channelDetails.center_image?.url;

  const shouldNotIndex = is_private || isCategoryFinance;
  const nonSecureOGImageUrl = channelImageUrl?.replace("https://", "http://");

  const isOneTimePlan = channelDetails?.plans?.length === 0;

  const firstChannelPlan = channelDetails?.plans?.[0];

  let channelFee;

  if (isOneTimePlan) {
    channelFee = channelDetails?.channel_fee;
  } else {
    channelFee =
      firstChannelPlan?.amount_after_discount || firstChannelPlan?.amount;
  }

  function sendViewContentEventViaPixel() {
    const eventDataForPixel = {
      content_ids: [channelDetails.uid],
      contents: [{ ...channelDetails, id: channelDetails.uid }],
      value: is_paid ? channelFee : 0,
    };

    trackStandardEvent(
      STANDARD_EVENTS.ViewContent,
      eventDataForPixel.content_ids,
      eventDataForPixel.contents,
      eventDataForPixel.value
    );
  }

  function renderChannelScreen() {
    let view = null;

    if (is_telegram) {
      view = (
        <TelegramChannelView
          channelDetails={channelDetails}
          isWhiteTheme={isWhiteTheme}
          customConfig={customConfig}
        />
      );
    } else if (is_whatsapp) {
      view = (
        <WhatsappChannelView
          channelDetails={channelDetails}
          isWhiteTheme={isWhiteTheme}
          customConfig={customConfig}
        />
      );
    } else {
      view = (
        <DefaultChannelView
          channelDetails={channelDetails}
          customConfig={customConfig}
        />
      );
    }

    return view;
  }

  useEffect(() => {
    const tags = {};
    category?.map((tag, id) => {
      if (tag.name) {
        tags[`category_${id + 1}`] = tag.name;
      }
    });

    const telegramChannelTrackDetails = is_telegram
      ? {
          telegram_chat_type: channelDetails.telegram?.telegram_chat_type,
          telegram_state: channelDetails.telegram?.state,
        }
      : {};

    const trackDetails = {
      page_name: "Channel Details",
      uid: uid,
      "creator.uid": creator.uid,
      "creator.full_name": creator.fullname,
      "creator.username": creator.username,
      is_verified_creator: creator.is_verified_creator,
      name: name,
      short_url_flutter: short_url_flutter,
      is_paid: is_paid,
      is_private: is_private,
      channel_status: cta_button.button_text,
      has_limited_seats: has_limited_seats,
      total_seats_left: total_seats_left,
      is_telegram: is_telegram,
      ...telegramChannelTrackDetails,
      ...tags,
    };

    Track.pageView(pageview.channel_details, trackDetails);

    sendViewContentEventViaPixel();

    addTagsInRecording(trackDetails);

    return () => {
      Track.clearProperties();
    };
  }, [channelDetails]);

  const setCountry = useStore((state) => state.setCountry);

  useEffect(() => {
    setCountry(country);
  }, [country]);


  return (
    <>
      {customCreatorConfig?.fb_pixel ? (
        <FBPixelScript FB_PIXEL_IDS={customCreatorConfig.fb_pixel.FB_PIXEL_IDS} />
      ) : null}
      {customCreatorConfig?.google_analytics ? (
        <GoogleAnalyticsScript
          GA_TRACKING_ID={customCreatorConfig.google_analytics.GA_TRACKING_ID}
        />
      ) : null}
      <NextSeo
        title={`${
          channelDetails.creator.fullname || channelDetails.creator.username
        } • ${channelDetails.name}`}
        description={channelDetails.description || `Join me on qoohoo`}
        openGraph={{
          title: `${
            channelDetails.creator.fullname || channelDetails.creator.username
          } • ${channelDetails.name}`,
          url: channelDetails.short_url_flutter,
          description: channelDetails.description || "Join me on qoohoo",
          images: channelImageUrl
            ? [
                // {
                //   url:
                //     channelDetails.shareable_card_url ||
                //     channelImageUrl,
                //   alt: channelDetails.name,
                // },
                {
                  url: nonSecureOGImageUrl,
                  secureUrl: channelImageUrl,
                  alt: channelDetails.name,
                  type: getOGImageType(channelImageUrl),
                  height: 640,
                  width: 640,
                },
              ]
            : [
                {
                  url: undefined,
                  secureUrl: undefined,
                  alt: undefined,
                  type: undefined,
                  height: undefined,
                  width: undefined,
                },
              ],
        }}
        noindex={shouldNotIndex}
        nofollow={shouldNotIndex}
        additionalMetaTags={
          channelImageUrl
            ? [
                {
                  property: "og:image",
                  content: channelImageUrl,
                },
              ]
            : []
        }
      />

      {/* renders the view based on channel type */}
      {renderChannelScreen()}
      <OrderSummaryModal />
    </>
  );
}
